/**
 * Modelo y parser del `payloadJson` de las notificaciones de **costo actualizado**
 * (`inventory.cost-updated`, `purchasing.cost-updated`, `pricing.price-updated`), spec §4.
 *
 * El `NotificationDto.payloadJson` es un `string` sin tipar en el SDK (OpenAPI no tipa el JSON
 * embebido), así que modelamos aquí la forma esperada y parseamos de forma **defensiva**: cualquier
 * payload que no traiga un array `lists` se considera "no es de costo" y devuelve `null` (el
 * contrato no está cerrado y otros tipos de notificación traen otros payloads).
 *
 * Contrato pendiente de confirmar con backend (CONFIRMAR 2 de
 * `docs/plans/2026-07-02-recalculo-precios-notificaciones-necesidades-backend.md`).
 */

/** Qué se recalculó en la lista: `null` = cambio manual (no se recalculó nada). */
export type RecalculationTarget = 'SalePrice' | 'Margin';

/** Fila del desglose por lista de precios afectada. */
export interface CostUpdatedListEntry {
  priceId: string;
  listPriceId: string;
  listPriceName: string;
  /** Código legible (ej. `"USD"`), guía 2026-07-02. Opcional: notificaciones antiguas no lo traen. */
  currencyCode?: string | null;
  /** Símbolo legible (ej. `"$"`), guía 2026-07-02. Opcional: notificaciones antiguas no lo traen. */
  currencySymbol?: string | null;
  previousCost: number;
  newCost: number;
  previousPriceOfSale: number;
  newPriceOfSale: number;
  /** `null` en cambios manuales (no había recálculo previo). Confirmado backend §8.2. */
  previousMarginPercentage: number | null;
  newMarginPercentage: number | null;
  negativeMargin: boolean;
  recalculationTarget: RecalculationTarget | null;
}

/** Payload completo de una notificación de costo actualizado. */
export interface CostUpdatedPayload {
  itemId: string;
  itemVariantId: string;
  sku: string;
  itemName: string;
  /** AverageCost | LastInventoryCost | LastPurchaseCost | Manual */
  source: string;
  /** InventoryMovement | PurchaseReceipt | Manual */
  originDocumentType: string;
  originDocumentId: string;
  originDocumentNumber: string;
  hasNegativeMargin: boolean;
  lists: CostUpdatedListEntry[];
}

/**
 * Parsea el `payloadJson` de una notificación de costo. Devuelve `null` si:
 * el string es vacío/nulo, no es JSON válido, o no tiene la forma esperada (array `lists`).
 * Nunca lanza.
 */
export function parseCostUpdatedPayload(payloadJson: string | null | undefined): CostUpdatedPayload | null {
  if (!payloadJson) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(payloadJson);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null) return null;
  const obj = parsed as Record<string, unknown>;
  if (!Array.isArray(obj['lists'])) return null;

  return obj as unknown as CostUpdatedPayload;
}

/**
 * `true` si la notificación tiene un payload de costo con al menos una lista afectada
 * (criterio para ofrecer la vista de detalle con la tabla `lists[]`).
 */
export function hasCostUpdatedDetail(payloadJson: string | null | undefined): boolean {
  const payload = parseCostUpdatedPayload(payloadJson);
  return !!payload && payload.lists.length > 0;
}

/**
 * Etiqueta de moneda para mostrar en la UI. Desde el breaking de 2026-07-21 el precio ya no tiene
 * moneda propia y el payload no trae `currencyId`: solo se muestra el `currencyCode` (moneda
 * principal del tenant). Devuelve `''` si el backend no lo envía (tenant sin datos fiscales).
 */
export function currencyLabel(entry: Pick<CostUpdatedListEntry, 'currencyCode'>): string {
  return entry.currencyCode ?? '';
}
